import React, { useEffect, useMemo, useState } from "react";
import "../ScrollableMapWithDecor.css";
import mapData from "./mapIserba.json";
import {
	generateBiomeCanvasDataUrl,
	generateFogWave,
	getSmoothPath,
	renderDecorShape,
} from "../mapUtils";
import { createNoise2D } from "simplex-noise";
import alea from "alea";
import { fetchScoresForQuizIds } from "services/quizResults";
import { useNavigate } from "react-router-dom";
import {
	BodySVGs,
	HairSVGs,
	ClothesSVGs,
} from "components/onboarding/iserba/iserbaAvatarIconsMap";
import { MdEdit } from "react-icons/md";
import { fetchQuizzesByIds } from "services/quizzes";

const quizIds = [
	"085787d1-2ac2-450e-bc2b-3ce50906fb01",
	"5376a517-4255-4f40-b3ee-777b232902dd",
	"5680955c-2187-454b-8892-892a064b23c2",
	"d58b69c0-1c30-4473-a1f2-668305ef3f93",
];

const biomeNoise = createNoise2D(alea("biome-seed"));

const IserbaProgressMap = ({ handleVerifyPlayer, playerAlreadyPlayed }) => {
	const navigate = useNavigate();

	// const quizzes = JSON.parse(localStorage.getItem("quizzes") || "[]");
	const [quizzes, setQuizzes] = useState([]);
	const [accessibleQuiz, setAccessibleQuiz] = useState(null);

	const now = new Date();

	const [biomeDataUrl, setBiomeDataUrl] = useState(null);
	const [players, setPlayers] = useState([]);
	const [player, setPlayer] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem("player") || "{}");
		} catch {
			return {};
		}
	});

	const customPoints = useMemo(() => mapData.path || [], []);
	const decor = useMemo(() => mapData.decor || [], []);
	const mapWidth = useMemo(() => {
		const maxPointX = Math.max(...customPoints.map((p) => p.x), 0) + 50;
		return Math.max(window.innerWidth, maxPointX);
	}, [customPoints]);

	const pathD = useMemo(() => getSmoothPath(customPoints), [customPoints]);
	const maxPlayerIndex = useMemo(
		() => Math.max(...players.map((p) => p.totalScore), 0),
		[players]
	);
	const fogLimitIndex = maxPlayerIndex + 15;

	useEffect(() => {
		setBiomeDataUrl(generateBiomeCanvasDataUrl(mapWidth, 200, biomeNoise));
	}, [mapWidth]);

	useEffect(() => {
		const fetchScores = async () => {
			const { data } = await fetchScoresForQuizIds(quizIds);
			if (!data) return;

			const unique = new Map();
			for (const entry of data) {
				const key = `${entry.player_id}-${entry.quiz_id}`;
				if (!unique.has(key)) unique.set(key, entry);
			}

			const playerTotals = {};
			for (const entry of unique.values()) {
				if (!playerTotals[entry.player_id]) {
					playerTotals[entry.player_id] = { totalScore: 0 };
				}
				playerTotals[entry.player_id].totalScore += entry.score;
			}

			const sorted = Object.entries(playerTotals)
				.map(([id, { totalScore }]) => ({ id: parseInt(id), totalScore }))
				.sort((a, b) => b.totalScore - a.totalScore);

			setPlayers(sorted);

			const self = sorted.find((p) => p.id === player.id);
			setPlayer((prev) => ({
				...prev,
				totalScore: self?.totalScore || 0,
			}));
		};

		if (player.id) fetchScores();
	}, [player.id]);

	useEffect(() => {
		let isMounted = true;

		const now = new Date();

		const findAccessibleQuiz = (quizList) => {
			return quizList.find(
				(quiz) =>
					new Date(quiz.accessible_from) <= now &&
					new Date(quiz.accessible_until) >= now
			);
		};

		const loadQuizzes = async () => {
			const { data, error } = await fetchQuizzesByIds(quizIds);

			if (error) {
				console.error("Error fetching quizzes:", error);
				return;
			}

			if (isMounted && data) {
				localStorage.setItem("quizzes", JSON.stringify(data));
				setQuizzes(data);

				const todayQuiz = findAccessibleQuiz(data) || data[0];
				if (todayQuiz) {
					localStorage.setItem("quiz", JSON.stringify(todayQuiz));
					setAccessibleQuiz(todayQuiz);
					navigate(`/start/${todayQuiz.id}`, { replace: true });
				}
			}
		};

		const storedQuizzes = JSON.parse(localStorage.getItem("quizzes") || "[]");

		if (storedQuizzes.length) {
			setQuizzes(storedQuizzes);
			const todayQuiz = findAccessibleQuiz(storedQuizzes);

			if (todayQuiz) {
				localStorage.setItem("quiz", JSON.stringify(todayQuiz));
				setAccessibleQuiz(todayQuiz);
				navigate(`/start/${todayQuiz.id}`, { replace: true });
			}
		} else {
			loadQuizzes();
		}

		return () => {
			isMounted = false;
		};
	}, []);

	const handleReplayQuiz = (quizId) => {
		const quizzes = JSON.parse(localStorage.getItem("quizzes") || "[]");
		const quiz = quizzes.find((q) => q.id === quizId.trim());

		if (quiz) {
			localStorage.setItem("quiz", JSON.stringify(quiz));
			navigate(`/play/${quizId.trim()}`);
		} else {
			console.warn("Quiz not found in localStorage:", quizId);
		}
	};

	return (
		<div
			style={{
				height: "calc(var(--vh, 1vh) * 100)",
				paddingTop: "env(safe-area-inset-top)",
				paddingBottom: "env(safe-area-inset-bottom)",
			}}
		>
			<div className="scrollable-map-container relative">
				<svg
					className="scrollable-map-svg"
					viewBox={`0 0 ${mapWidth} 200`}
					xmlns="http://www.w3.org/2000/svg"
					style={{ background: "#eee", cursor: "default" }}
				>
					{biomeDataUrl && (
						<image
							href={biomeDataUrl}
							x="0"
							y="0"
							width={mapWidth}
							height={200}
						/>
					)}

					<path d={pathD} fill="none" stroke="#D6C397" strokeWidth="10" />
					{/* <path d={pathD} fill="none" stroke="#C2A877" strokeWidth="6" /> */}

					{customPoints.map((p, i) => (
						<circle key={i} cx={p.x} cy={p.y} r="2" fill="#9C865F" />
					))}

					{decor
						.map((d, i) => ({ ...d, originalIndex: i }))
						.sort((a, b) => a.y - b.y)
						.map((d, i) => (
							<g key={i}>{renderDecorShape(d)}</g>
						))}

					{Object.entries(
						players.reduce((acc, p) => {
							acc[p.totalScore] = acc[p.totalScore] || [];
							acc[p.totalScore].push(p);
							return acc;
						}, {})
					).map(([score, grouped]) => {
						const point = customPoints[parseInt(score)];
						if (!point) return null;
						return (
							<g key={score}>
								<rect
									x={point.x - 10}
									y={point.y - 1}
									width="20"
									height="4"
									rx="2"
									fill="white"
								/>
								<text
									x={point.x}
									y={point.y + 2}
									fontSize="3"
									fontWeight="bold"
									fill="#000"
									textAnchor="middle"
								>
									{grouped.length} joueur{grouped.length > 1 ? "s" : ""}
								</text>
							</g>
						);
					})}

					{player.id && customPoints[player.totalScore] && (
						<g>
							<svg
								x={customPoints[player.totalScore].x - 25}
								y={customPoints[player.totalScore].y - 40}
								height={50}
								width={50}
								viewBox="0 0 150 400"
							>
								{BodySVGs[player.avatar?.gender]?.(player.avatar?.skin_color)}
								{HairSVGs[player.avatar?.gender]?.[player.avatar?.hair_shape]?.(
									player.avatar?.hair_color
								)}
								{ClothesSVGs[player.avatar?.gender]?.[
									player.avatar?.clothes
								]?.()}
							</svg>
							<g>
								<rect
									x={customPoints[player.totalScore].x - 30}
									y={customPoints[player.totalScore].y + 4}
									width="60"
									height="10"
									rx="4"
									fill="white"
								/>
								<text
									x={customPoints[player.totalScore].x}
									y={customPoints[player.totalScore].y + 8}
									fontSize="3"
									fontWeight="bold"
									fill="#000"
									textAnchor="middle"
								>
									{player.pseudo}
								</text>
								<text
									x={customPoints[player.totalScore].x}
									y={customPoints[player.totalScore].y + 12}
									fontSize="3"
									fontWeight="bold"
									fill="#000"
									textAnchor="middle"
								>
									{player.totalScore} points
								</text>
							</g>
						</g>
					)}

					{/* Fog */}
					{customPoints[fogLimitIndex]?.x && (
						<>
							<path
								d={generateFogWave(customPoints[fogLimitIndex].x, mapWidth)}
								fill="white"
								opacity="0.65"
							/>
							<path
								d={generateFogWave(
									customPoints[fogLimitIndex].x + 20,
									mapWidth,
									200,
									10,
									15
								)}
								fill="white"
								opacity="0.75"
							/>
							<path
								d={generateFogWave(
									customPoints[fogLimitIndex].x + 50,
									mapWidth,
									200,
									5,
									20
								)}
								fill="white"
								opacity="0.85"
							/>
						</>
					)}
				</svg>

				<div className="fixed top-4 right-4 flex items-center justify-start bg-white  rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-sm p-2 overflow-hidden">
					<div className="h-[60px] w-[80px] flex items-center justify-center overflow-hidden">
						<svg viewBox="0 -20 40 80">
							{BodySVGs[player.avatar.gender](player.avatar.skin_color)}
							{HairSVGs[player.avatar.gender][player.avatar.hair_shape](
								player.avatar.hair_color
							)}
							{ClothesSVGs[player.avatar.gender][player.avatar.clothes]()}
						</svg>
					</div>
					<button
						className="absolute top-0 right-0 rounded-bl-lg px-2 py-1 border-black text-white bg-[#fbb554]  hover:bg-[#ffa256] text-xs"
						onClick={handleVerifyPlayer}
					>
						<MdEdit />
					</button>
				</div>

				<div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center justify-center gap-2 bg-white p-4 px-6 rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-sm">
					{accessibleQuiz && accessibleQuiz.id && !playerAlreadyPlayed ? (
						<button
							className="rounded-lg px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256]"
							onClick={() => handleReplayQuiz(accessibleQuiz.id)}
						>
							Jouer {accessibleQuiz.name}
						</button>
					) : quizzes.filter((quiz) => new Date(quiz.accessible_from) <= now)
							.length > 0 ? (
						quizzes
							.filter((quiz) => new Date(quiz.accessible_from) <= now)
							.map((quiz) => (
								<button
									key={quiz.id}
									className="rounded-lg px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256]"
									onClick={() => {
										handleReplayQuiz(quiz.id);
									}}
								>
									Jouer {quiz.name}
								</button>
							))
					) : (
						<span className="text-gray-600 font-bold">
							Revenez demain pour commencer à jouer
						</span>
					)}
				</div>

				{[3523, 3521].includes(player.id) && (
					<div className="fixed z-50 bottom-4 left-1/2 -translate-x-1/2 flex items-center justify-center gap-2 bg-white p-4 px-6 rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-sm">
						{quizzes.map((quiz) => (
							<button
								key={quiz.id}
								className="rounded-lg px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256]"
								onClick={() => {
									handleReplayQuiz(quiz.id);
								}}
							>
								Rejouer {quiz.name}
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default IserbaProgressMap;
